import { ChangeEvent, Dispatch, forwardRef, useEffect, useState } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { useTranslation } from 'react-i18next'

import OnlinePayment from '@/components/PaymentDetails/OnlinePayment'
import publicConfig from '@/config/public'
import { VenueBranding } from '@/libs/helpers/adapters'
import {
  CardCountry,
  GiftCard,
  GiftCardRequest,
  OrderPayment,
  PaymentMethod,
  StreetAddressState,
  TBPaymentType,
} from '@/libs/helpers/apiClient'
import { OrderMethod, OrderProvider } from '@/libs/helpers/utils'
import { OrderingAddress } from '@/providers/googleMaps'
import { Grid, Option, Radio, Text } from '@/tbui'

import GiftCardPicker from './GiftCardPicker'

// There is no way to extend enums in TS
enum ExtraPaymentMethod {
  DEBIT_CREDIT = 'DEBIT_CREDIT',
}
// But you can actually merge 2 enums in one type
type ExtendedPaymentMethod = PaymentMethod | ExtraPaymentMethod

interface PaymentDetailsProps {
  onChange?: (orderPayment: OrderPayment) => void
  paymentMethods: PaymentMethod[]
  setWePay: Dispatch<WePayIframe>
  wePayIframe: WePayIframe | null
  isScheduled: boolean
  isDGCEnabled: boolean
  providerXRefID: OrderProvider
  isOODev: boolean
  isValid?: boolean
  disableTip: boolean
  attachedGiftCards?: GiftCard[]
  selectedGiftCardCode?: string | null
  handleAddGiftCard: ({ code, recaptchaToken }: GiftCardRequest) => void
  applyGiftCardError: string | null
  handleSelectGiftCard?: (giftCardCode: string | null) => void
  showOnlinePayment: boolean
  venueCountry: string
  isCartLoading: boolean
  isEmptyCart: boolean
  totalAfterTax: string
  streetAddress: StreetAddressState | null
  setStreetAddress: (value: StreetAddressState | null) => void
  zipCode: string
  setZipCode: (value: string) => void
  validBillingAddress: boolean
  setValidZipCode: (value: boolean) => void
  setValidBillingAddress: (value: boolean) => void
  tbPaymentType: TBPaymentType | null
  deliveryAddress?: OrderingAddress
  orderMethod: OrderMethod
  payrixSDKLoaded: boolean
  venueBranding?: VenueBranding
}

// eslint-disable-next-line react/display-name
const PaymentDetails = forwardRef<HTMLDivElement, PaymentDetailsProps>(
  (
    {
      paymentMethods = [],
      providerXRefID,
      isValid = true,
      isScheduled = false,
      isDGCEnabled = false,
      isOODev,
      setWePay,
      wePayIframe,
      onChange,
      disableTip,
      selectedGiftCardCode,
      attachedGiftCards = [],
      handleAddGiftCard,
      applyGiftCardError,
      handleSelectGiftCard,
      showOnlinePayment,
      venueCountry,
      isCartLoading,
      isEmptyCart,
      totalAfterTax,
      streetAddress,
      setStreetAddress,
      zipCode,
      setZipCode,
      validBillingAddress,
      setValidZipCode,
      setValidBillingAddress,
      tbPaymentType,
      deliveryAddress,
      orderMethod,
      payrixSDKLoaded,
      venueBranding,
    },
    ref
  ) => {
    // STATES
    const [cardCountry, setCardCountry] = useState<CardCountry>(
      venueCountry === CardCountry.US ? CardCountry.US : CardCountry.CA
    )
    const [isValidPostal, setIsValidPostal] = useState<boolean>(isValid)
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>()
    const [postalOrZipCode, setPostalOrZipCode] = useState<string>('')
    const [radioOptions, setRadioOptions] = useState<Option[]>([])
    const [tipAmount, setTipAmount] = useState<string>('0.00')
    const [isTipSelected, setIsTipSelected] = useState<boolean>(
      disableTip === false || wePayIframe == null
    )
    const [isWePaySDKLoaded, setIsWePaySDKLoaded] = useState<boolean>(false)
    const [hasWePayError, setHasWePayError] = useState<boolean>(false)

    // HOOKS
    const { t } = useTranslation()

    // CONSTS
    const isOnlinePayment = paymentMethod === PaymentMethod.ONLINE

    // HANDLERS
    const getValidPaymentMethod = (pmtMethod: ExtendedPaymentMethod): PaymentMethod => {
      return pmtMethod === ExtraPaymentMethod.DEBIT_CREDIT ? PaymentMethod.CREDIT : pmtMethod
    }

    const handlePaymentChange = (e: ChangeEvent<HTMLInputElement>): void => {
      const pmtMethod = e.target.value as PaymentMethod
      const validPaymentMethod = getValidPaymentMethod(pmtMethod)
      setPaymentMethod(validPaymentMethod as PaymentMethod)
    }

    const handleDGCChange = (e: ChangeEvent<HTMLInputElement>): void => {
      if (handleSelectGiftCard == null) {
        return
      }

      if (e.target.checked) {
        const gCard = attachedGiftCards.find((card) => card.code === e.target.id)
        if (gCard != null) {
          handleSelectGiftCard(gCard.code)
        }
      } else {
        handleSelectGiftCard(null)
      }
    }

    const handleSetTip = (_tipAmount: string): void => {
      setIsTipSelected(true)
      setTipAmount(_tipAmount)
    }

    const filterPaymentMethodOptions = (options: PaymentMethod[]): ExtendedPaymentMethod[] => {
      // filter non-ONLINE if orderMethod is a doordash delivery
      const isDoorDash = providerXRefID === OrderProvider.TOUCHBISTRO_DOORDASH
      if (!isOODev && (isScheduled || isDoorDash)) {
        // if scheduled we only allow the user to pay online
        return options.filter(
          (pmtMethod: PaymentMethod): boolean => pmtMethod === PaymentMethod.ONLINE
        )
      }

      // Remove the online payment method to add it to the beginning of the list of options
      const optionsWithoutOnlinePayment = options.filter(
        (pmtMethod: PaymentMethod): boolean => pmtMethod !== PaymentMethod.ONLINE
      )
      let optionsPrependedWithOnlinePayment = optionsWithoutOnlinePayment

      // If the venue has online payment, add it to the beginning
      if (options.includes(PaymentMethod.ONLINE)) {
        optionsPrependedWithOnlinePayment = [PaymentMethod.ONLINE, ...optionsWithoutOnlinePayment]
      }

      // the screen just fits a max of 3 paymentMethods
      const fitsTheScreenLimit = options.length < 4
      if (fitsTheScreenLimit) {
        return optionsPrependedWithOnlinePayment
      }

      // If there is more then 3 payment methods
      // Removes CREDIT and DEBIT
      const filteredCardOptions = optionsPrependedWithOnlinePayment.filter(
        (pmtMethod: PaymentMethod): boolean =>
          pmtMethod !== PaymentMethod.CREDIT && pmtMethod !== PaymentMethod.DEBIT
      )

      // and then adds DEBIT_CREDIT option
      return [...filteredCardOptions, ExtraPaymentMethod.DEBIT_CREDIT]
    }

    const mapPaymentMethods = (pmtMethods: PaymentMethod[]): Option[] => {
      const filteredOptions = filterPaymentMethodOptions(pmtMethods)
      return filteredOptions.map((pmtMethod: ExtendedPaymentMethod): Option => {
        const normalizedMethod = pmtMethod.toLowerCase()
        return {
          key: normalizedMethod,
          label: t(`checkout.payment.option_${normalizedMethod}`, {
            context: orderMethod.toLowerCase(),
          }),
          value: pmtMethod,
          disabled: isCartLoading,
        }
      })
    }

    // EFFECTS
    useEffect(() => {
      setIsValidPostal(isValid)
    }, [isValid])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (typeof WePay !== 'undefined' && !isWePaySDKLoaded && !hasWePayError) {
        try {
          // configures app with wepay sdk
          const initError = WePay.configure(
            publicConfig.environment === 'production' ? publicConfig.environment : 'stage',
            publicConfig.wepaySDK,
            '3.0'
          )
          if (initError != null) {
            /* eslint-disable no-console */
            // eslint-disable-next-line no-restricted-properties
            setHasWePayError(true)
            /* eslint-enable no-console */
          } else {
            setIsWePaySDKLoaded(true)
          }
        } catch (error) {
          datadogRum.addError(error)
          setHasWePayError(true)
        }
      }
    })

    useEffect(() => {
      if (paymentMethods.length > 0) {
        const options = mapPaymentMethods(paymentMethods)
        setRadioOptions(options)

        const defaultPaymentMethod = options[0]?.value as ExtendedPaymentMethod
        // if a user set up the paymentMethod previously, pass the same value
        // if it's initial load of the component (payment method is undefined),
        // default the value to first option
        const validPaymentMethod = paymentMethod ?? getValidPaymentMethod(defaultPaymentMethod)
        setPaymentMethod(validPaymentMethod)

        if (onChange && isTipSelected) {
          onChange({ paymentMethod: validPaymentMethod, tipAmount })
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentMethods, isScheduled, isCartLoading])

    useEffect(() => {
      if (onChange != null && paymentMethod != null) {
        setHasWePayError(false)
        if (isOnlinePayment) {
          // wait for payment method to be set and tip to finish calculating, then call for cart update
          if (isTipSelected) {
            onChange({
              postalCode: postalOrZipCode,
              country: cardCountry,
              paymentMethod,
              tipAmount,
            })
          }
        } else {
          onChange({
            paymentMethod,
          })
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnlinePayment, tipAmount, postalOrZipCode, cardCountry, paymentMethod, isTipSelected])

    return (
      <Grid container data-test="payment-details">
        <Text type="H2" m="0 0 16px 0">
          {t('checkout.payment.title')}
        </Text>
        {radioOptions.length === 0 ? (
          <Text type="H4" bold={false}>
            {t('checkout.payment.disabled', { context: orderMethod.toLowerCase() })}
          </Text>
        ) : (
          <Radio
            template={`repeat(${radioOptions.length}, 1fr)`}
            onChange={handlePaymentChange}
            options={radioOptions}
            name="payment-options"
            selected={radioOptions[0].value}
          />
        )}

        {isOnlinePayment ? (
          <Grid ref={ref}>
            <OnlinePayment
              setPostalOrZipCode={setPostalOrZipCode}
              paymentMethod={PaymentMethod.ONLINE}
              setIsValidPostal={setIsValidPostal}
              postalOrZipCode={postalOrZipCode}
              setCardCountry={setCardCountry}
              isValidPostal={isValidPostal}
              setTipAmount={handleSetTip}
              cardCountry={cardCountry}
              setWePay={setWePay}
              isWePaySDKLoaded={isWePaySDKLoaded}
              hasWePayError={hasWePayError}
              setHasWePayError={setHasWePayError}
              wePayIframe={wePayIframe}
              disableTip={disableTip}
              handleDGCChange={handleDGCChange}
              giftCardOptions={attachedGiftCards}
              isGiftCardPicked={selectedGiftCardCode != null}
              isDGCEnabled={isDGCEnabled}
              selectedGiftCardCode={selectedGiftCardCode}
              handleAddGiftCard={handleAddGiftCard}
              applyGiftCardError={applyGiftCardError}
              showOnlinePayment={showOnlinePayment}
              isCartLoading={isCartLoading}
              isEmptyCart={isEmptyCart}
              totalAfterTax={totalAfterTax}
              tbPaymentType={tbPaymentType}
              deliveryAddress={deliveryAddress}
              streetAddress={streetAddress}
              setStreetAddress={setStreetAddress}
              zipCode={zipCode}
              setZipCode={setZipCode}
              validBillingAddress={validBillingAddress}
              setValidZipCode={setValidZipCode}
              setValidBillingAddress={setValidBillingAddress}
              orderMethod={orderMethod}
              payrixSDKLoaded={payrixSDKLoaded}
              venueBranding={venueBranding}
            />
          </Grid>
        ) : (
          isDGCEnabled && (
            <GiftCardPicker
              title={t('checkout.payment.pay_gift_card_bill')}
              isCartLoading={isCartLoading}
              handleDGCChange={handleDGCChange}
              giftCardOptions={attachedGiftCards}
              isGiftCardPicked={selectedGiftCardCode != null}
              selectedGiftCardCode={selectedGiftCardCode}
              handleAddGiftCard={handleAddGiftCard}
              applyGiftCardError={applyGiftCardError}
            />
          )
        )}
      </Grid>
    )
  }
)

export default PaymentDetails
